import React, { useState, useEffect } from 'react';
import { X, Play, MessageSquare, AlertTriangle } from 'lucide-react';

const AiRoleTestModal = ({ isOpen, onClose, roleKey, roleLabel, initialPrompt }) => {
    const [prompt, setPrompt] = useState('');
    const [response, setResponse] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Reset state when opening for a different role
    useEffect(() => {
        if (isOpen) {
            setPrompt(initialPrompt || getDefaultPrompt(roleKey));
            setResponse('');
            setError(null);
        }
    }, [isOpen, roleKey, initialPrompt]);

    const getDefaultPrompt = (key) => {
        switch (key) {
            case 'sql_generator': return "Wie viele Produkte kosten mehr als 50 Euro?";
            case 'rag_answer': return "Welche technischen Daten hat das Modell Fit20F?";
            case 'seo_description': return "Erstelle einen SEO-Text für das Produkt 'SuperCharger X'.";
            case 'voice_answer': return "Wie ist der Status von Auftrag 123?";
            case 'document_analysis': return "(This role usually requires a file upload. Testing text extraction logic only.)\nExtract data from this text: 'Invoice #999, Total: $500'";
            default: return "Hello, are you working?";
        }
    };

    const handleTest = async () => {
        if (!prompt.trim()) return;
        setLoading(true);
        setResponse('');
        setError(null);

        try {
            const res = await fetch('/api/admin/ai/test', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ role: roleKey, prompt: prompt })
            });
            const data = await res.json();

            if (data.success) {
                setResponse(data.response);
            } else {
                setError(data.error || "Unknown error occurred");
            }
        } catch (err) {
            setError(err.message || "Network request failed");
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
            <div className="bg-stone-900 border border-stone-700 w-full max-w-2xl rounded-xl shadow-2xl flex flex-col max-h-[90vh]">
                {/* Header */}
                <div className="flex justify-between items-center p-4 border-b border-stone-800 bg-stone-900/50 rounded-t-xl">
                    <div className="flex items-center gap-3">
                        <div className="bg-purple-900/30 p-2 rounded text-purple-400">
                            <Play size={20} />
                        </div>
                        <div>
                            <h3 className="font-bold text-white">Test Role: {roleLabel}</h3>
                            <p className="text-xs text-stone-500 font-mono">{roleKey}</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="text-stone-500 hover:text-white p-1 rounded hover:bg-stone-800 transition">
                        <X size={20} />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 overflow-y-auto flex-1 space-y-6">
                    {/* Input */}
                    <div>
                        <label className="block text-xs font-bold text-stone-400 mb-2 uppercase tracking-wide">Test Prompt</label>
                        <textarea
                            className="w-full bg-black/30 border border-stone-700 rounded-lg p-4 text-stone-200 focus:border-purple-500 focus:outline-none min-h-[100px] font-mono text-sm leading-relaxed"
                            value={prompt}
                            onChange={(e) => setPrompt(e.target.value)}
                            placeholder="Enter a prompt to test this AI configuration..."
                        />
                    </div>

                    {/* Output */}
                    <div>
                        <label className="block text-xs font-bold text-stone-400 mb-2 uppercase tracking-wide">
                            AI Response
                            {loading && <span className="ml-2 text-purple-400 animate-pulse">Generating...</span>}
                        </label>
                        <div className={`
                            w-full min-h-[150px] rounded-lg p-4 border text-sm font-mono whitespace-pre-wrap
                            ${error
                                ? 'bg-red-900/10 border-red-800 text-red-300'
                                : 'bg-stone-950/50 border-stone-800 text-stone-300'
                            }
                        `}>
                            {error ? (
                                <div className="flex items-center gap-2">
                                    <AlertTriangle size={16} /> {error}
                                </div>
                            ) : (
                                response || <span className="text-stone-600 italic">Response will appear here...</span>
                            )}
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-stone-800 bg-stone-900/50 rounded-b-xl flex justify-end">
                    <button
                        onClick={handleTest}
                        disabled={loading || !prompt.trim()}
                        className={`
                            px-6 py-2 rounded-lg font-bold flex items-center gap-2 shadow-lg transition-all
                            ${loading || !prompt.trim()
                                ? 'bg-stone-800 text-stone-500 cursor-not-allowed'
                                : 'bg-purple-600 text-white hover:bg-purple-500 hover:scale-105'
                            }
                        `}
                    >
                        {loading ? <div className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin" /> : <Play size={16} />}
                        Run Test
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AiRoleTestModal;
